import { useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from 'urql';
import { AlertTriangle, Check, UploadCloud } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import SelectorCuenta from '../../../components/Tesoreria/SelectorCuenta';

const Q_EXTRACTOS = gql`
  query ExtractosBancarios($cuentaBancariaId: ID!) {
    extractosBancarios(cuentaBancariaId: $cuentaBancariaId, limite: 10) {
      id
      archivoNombre
      createdAt
      estado
      periodoDesde
      periodoHasta
    }
  }
`;

const Q_MOVS = gql`
  query MovimientosBancarios($extractoId: ID!, $soloNoConciliados: Boolean!, $search: String, $desde: String, $hasta: String) {
    movimientosBancarios(extractoId: $extractoId, soloNoConciliados: $soloNoConciliados, search: $search, desde: $desde, hasta: $hasta) {
      id
      fecha
      descripcion
      referencia
      monto
      tipo
      conciliado
      montoPendiente
    }
  }
`;

const Q_SUGERENCIAS = gql`
  query Sugerencias($extractoId: ID!) {
    sugerenciasConciliacion(extracto_id: $extractoId) {
      movimientoBancarioId
      candidatos {
        tipo
        id
        score
        motivo
        referencia
        monto
        fecha
      }
    }
  }
`;

const M_IMPORTAR = gql`
  mutation Importar($input: ImportarExtractoBancarioInput!) {
    importarExtractoBancario(input: $input) {
      ok
      movimientosInsertados
      extracto { id archivoNombre createdAt }
    }
  }
`;

const M_CONCILIAR = gql`
  mutation Conciliar($input: ConciliarMovimientoInput!) {
    conciliarMovimiento(input: $input) { id conciliado montoPendiente }
  }
`;

const M_DESCONCILIAR = gql`
  mutation Desconciliar($movimiento_bancario_id: ID!) {
    desconciliarMovimiento(movimiento_bancario_id: $movimiento_bancario_id) { id conciliado }
  }
`;

const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function Conciliaciones() {
  const [cuentaId, setCuentaId] = useState('');
  const [extractoId, setExtractoId] = useState('');
  const [sucursalId, setSucursalId] = useState('1');
  const [soloNoConciliados, setSoloNoConciliados] = useState(true);
  const [search, setSearch] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [selectedMovId, setSelectedMovId] = useState('');
  const [loadingImport, setLoadingImport] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [extractosRes, refetchExtractos] = useQuery({ query: Q_EXTRACTOS, variables: { cuentaBancariaId: cuentaId }, pause: !cuentaId });
  const [movsRes, refetchMovs] = useQuery({
    query: Q_MOVS,
    variables: { extractoId, soloNoConciliados, search: search || null, desde: desde || null, hasta: hasta || null },
    pause: !extractoId,
    requestPolicy: 'network-only',
  });
  const [sugRes] = useQuery({ query: Q_SUGERENCIAS, variables: { extractoId }, pause: !extractoId, requestPolicy: 'network-only' });

  const [, importarExtracto] = useMutation(M_IMPORTAR);
  const [, conciliarMovimiento] = useMutation(M_CONCILIAR);
  const [, desconciliarMovimiento] = useMutation(M_DESCONCILIAR);

  const movimientos = movsRes.data?.movimientosBancarios || [];
  const sugerenciasByMovimiento = useMemo(() => {
    const items = sugRes.data?.sugerenciasConciliacion || [];
    return new Map(items.map((item) => [String(item.movimientoBancarioId), item.candidatos]));
  }, [sugRes.data]);

  const selectedCandidatos = selectedMovId ? (sugerenciasByMovimiento.get(String(selectedMovId)) || []) : [];

  const handleImportFile = async (file) => {
    if (!file || !cuentaId) return;
    try {
      setLoadingImport(true);
      setErrorMsg('');
      const csvBase64 = await toBase64(file);
      const result = await importarExtracto({
        input: {
          sucursal_id: sucursalId,
          cuenta_bancaria_id: cuentaId,
          csv_base64: csvBase64,
          archivo_nombre: file.name,
          delimiter: ',',
          mapeo_columnas: {
            fecha_col: 'fecha',
            descripcion_col: 'descripcion',
            referencia_col: 'referencia',
            monto_col: 'monto',
            tipo_col: 'tipo',
          },
        },
      });
      if (result.error) throw result.error;
      setExtractoId(result.data.importarExtractoBancario.extracto.id);
      refetchExtractos({ requestPolicy: 'network-only' });
      refetchMovs({ requestPolicy: 'network-only' });
    } catch (error) {
      setErrorMsg(error.message || 'No se pudo importar el CSV.');
    } finally {
      setLoadingImport(false);
    }
  };

  const handleConciliarCandidato = async (candidato) => {
    if (!selectedMovId || !candidato) return;
    const mov = movimientos.find((m) => String(m.id) === String(selectedMovId));
    const monto = Math.min(Math.abs(mov?.monto || 0), Math.abs(candidato?.monto || 0));
    const result = await conciliarMovimiento({
      input: {
        movimiento_bancario_id: selectedMovId,
        matches: [{ match_tipo: candidato.tipo, match_id: candidato.id, monto }],
      },
    });
    if (!result.error) {
      refetchMovs({ requestPolicy: 'network-only' });
    }
  };

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <PageHeader title="Conciliación bancaria" subtitle="Importación de extracto CSV + match automático/manual" />
      <section className="max-w-[1400px] mx-auto px-4 py-6 space-y-4">
        <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4 grid grid-cols-12 gap-3">
          <div className="col-span-12 md:col-span-4">
            <SelectorCuenta value={cuentaId} onChange={(c) => setCuentaId(c?.id || '')} label="Cuenta bancaria" required />
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="text-sm text-[rgb(var(--muted))]">Sucursal</label>
            <input value={sucursalId} onChange={(e) => setSucursalId(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-2" />
          </div>
          <div className="col-span-6 md:col-span-2">
            <label className="text-sm text-[rgb(var(--muted))]">Extracto</label>
            <select value={extractoId} onChange={(e) => setExtractoId(e.target.value)} className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-2">
              <option value="">Seleccione</option>
              {(extractosRes.data?.extractosBancarios || []).map((ext) => (
                <option key={ext.id} value={ext.id}>{ext.archivoNombre || `Extracto #${ext.id}`}</option>
              ))}
            </select>
          </div>
          <div className="col-span-12 md:col-span-4">
            <label className="text-sm text-[rgb(var(--muted))]">Importar CSV</label>
            <label className="flex items-center justify-center gap-2 h-10 rounded-xl border border-dashed border-[rgb(var(--border))] cursor-pointer hover:bg-[rgba(var(--muted),0.2)]">
              <UploadCloud className="w-4 h-4" /> {loadingImport ? 'Importando…' : 'Seleccionar archivo'}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleImportFile(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        {errorMsg ? <div className="rounded-xl border border-[rgb(var(--danger))] bg-[rgba(var(--danger),0.12)] p-3 text-sm"><AlertTriangle className="inline w-4 h-4 mr-2" />{errorMsg}</div> : null}

        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 lg:col-span-8 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4">
            <div className="flex gap-2 mb-3">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar descripción/referencia" className="flex-1 rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-2" />
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-2" />
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg))] p-2" />
              <button onClick={() => setSoloNoConciliados((v) => !v)} className="rounded-xl border border-[rgb(var(--border))] px-3">{soloNoConciliados ? 'Solo no conciliados' : 'Todos'}</button>
            </div>
            {movsRes.fetching ? <div className="animate-pulse h-12 rounded-xl bg-[rgba(var(--muted),0.25)]" /> : null}
            <div className="max-h-[500px] overflow-auto divide-y divide-[rgb(var(--border))]">
              {movimientos.map((mov) => (
                <button key={mov.id} type="button" onClick={() => setSelectedMovId(String(mov.id))} className={`w-full text-left p-3 hover:bg-[rgba(var(--muted),0.2)] ${selectedMovId === String(mov.id) ? 'bg-[rgba(var(--brand),0.12)]' : ''}`}>
                  <div className="flex justify-between gap-2">
                    <div>
                      <p className="text-sm text-[rgb(var(--fg))]">{mov.fecha} · {mov.descripcion}</p>
                      <p className="text-xs text-[rgb(var(--muted))]">{mov.referencia || 'Sin referencia'} · {mov.tipo}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(mov.monto)}</p>
                      <p className="text-xs text-[rgb(var(--muted))]">Pendiente: {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(mov.montoPendiente)}</p>
                    </div>
                  </div>
                  {mov.conciliado ? <span className="inline-flex items-center text-xs mt-1"><Check className="w-3 h-3 mr-1" />Conciliado</span> : null}
                </button>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4">
            <h3 className="font-semibold mb-3">Sugerencias</h3>
            {!selectedMovId ? <p className="text-sm text-[rgb(var(--muted))]">Seleccione un movimiento para ver candidatos.</p> : null}
            <div className="space-y-2">
              {selectedCandidatos.map((cand) => (
                <div key={`${cand.tipo}-${cand.id}`} className="rounded-xl border border-[rgb(var(--border))] p-3">
                  <p className="text-sm font-medium">{cand.tipo} #{cand.id} · score {cand.score}</p>
                  <p className="text-xs text-[rgb(var(--muted))]">{cand.motivo}</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => handleConciliarCandidato(cand)} className="rounded-lg bg-[rgba(var(--brand),0.22)] px-2 py-1 text-xs">Conciliar</button>
                    <button onClick={() => desconciliarMovimiento({ movimiento_bancario_id: selectedMovId }).then(() => refetchMovs({ requestPolicy: 'network-only' }))} className="rounded-lg border border-[rgb(var(--border))] px-2 py-1 text-xs">Desconciliar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
